import type { BffSubmitResponse } from '@/services/rivaas-api';
import type { OnCompleteCallbackImages } from '@innovatrics/dot-smile-liveness';
import type { FailReasonValues } from '@repo/common';

import { Spacer } from '@dot/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { onAutoCaptureError } from '../utils/error';

import { SmileCamera } from './components/camera';
import { SmileCameraUi } from './components/camera-ui';

import { CaptureServerErrorOverlay } from '@/components/CaptureServerErrorOverlay/CaptureServerErrorOverlay';
import { ManualMessageUpload } from '@/components/ManualUpload/ManualMessageUpload';
import { Processing } from '@/components/Processing/Processing';
import { SecuredByBadge } from '@/components/SecuredByBadge/SecuredByBadge';
import { CaptureWrapper } from '@/components/capture-wrapper';
import { config } from '@/config';
import { useAppContext } from '@/providers/app-provider/context';
import { postSubmitHandled } from '@/services/rivaas-api';
import * as GSC from '@/styles/GlobalStyles';
import { applyBffSubmitResponse, buildSubmitErrorContext, retrySubmitErrorStep } from '@/utils/submit-error';
import { getPublicAsset } from '@/utils/utils';

/**
 * Manifest-driven `smile_capture` UI step (DOT web `Smile Liveness`).
 *
 * The DOT component delivers `Uint8Array content` — binary DIS payload (octet-stream), not raw MP4.
 * BFF accepts it on `POST /v1/submit` and forwards as a liveness record to DIS, where it
 * also sets the customer's main selfie via `selfieOrigin.link` if no other capture step has
 * provided one yet (see BFF `smile-capture.ts` handler + plan rule D7).
 */
export const SmileLiveness = () => {
  const { t } = useTranslation();
  const { dispatch, state } = useAppContext();
  // Mirrors the passive FaceCapture PROCESSING overlay: frozen last frame + "Processing"
  // text while the submit round-trips. Without it the DOT UI just vanishes on complete.
  const [processing, setProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<Blob | null>(null);
  const [serverErrorFailReason, setServerErrorFailReason] = useState<FailReasonValues | null>(null);
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
  const [serverErrorRetryable, setServerErrorRetryable] = useState(false);

  const redirectOptions = {
    unverifiedUrl: state.customerData?.redirect?.unverifiedUrl,
    verifiedUrl: state.customerData?.redirect?.verifiedUrl,
  };

  const upload = async (content: Uint8Array): Promise<BffSubmitResponse> => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);

    const blob = new Blob([new Uint8Array(content)], { type: 'application/octet-stream' });
    const data = await postSubmitHandled(blob);

    if (applyBffSubmitResponse(dispatch, data, redirectOptions)) {
      const errorContext = buildSubmitErrorContext(data);

      // Same reset the passive step does before its error overlay takes over.
      setProcessing(false);
      setCapturedImage(null);
      setServerErrorFailReason(errorContext?.failReason ?? null);
      setServerErrorMessage(errorContext?.message ?? null);
      setServerErrorRetryable(errorContext?.retryable ?? false);

      return data;
    }

    return data;
  };

  const handleComplete = (resultData: OnCompleteCallbackImages, content: Uint8Array) => {
    // Freeze the smile-phase frame (the last one the user saw) under the overlay.
    setCapturedImage(resultData.smilePhaseImageWithMetadata?.image ?? null);
    setProcessing(true);
    void upload(content);
  };

  const retryCapture = () => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);
    retrySubmitErrorStep(dispatch, state.submitErrorContext, redirectOptions);
  };

  return (
    <>
      <GSC.RelativeContainer>
        <CaptureWrapper>
          <GSC.CaptureContainer>
            <SmileCamera
              assetsDirectoryPath={getPublicAsset('/')}
              onComplete={handleComplete}
              onError={onAutoCaptureError}
            />
            <SmileCameraUi
              appStateInstructions={{
                loading: { text: t('common.state.loading'), visible: true },
                waiting: { text: '', visible: false },
              }}
              instructions={{
                candidate_selection: t('steps.faceCapture.instructions.candidateSelection'),
                face_centering: t('steps.faceCapture.instructions.faceCentering'),
                face_too_close: t('steps.faceCapture.instructions.faceTooClose'),
                face_too_far: t('steps.faceCapture.instructions.faceTooFar'),
                face_not_present: t('steps.faceCapture.instructions.faceNotPresent'),
              }}
            />
          </GSC.CaptureContainer>
        </CaptureWrapper>
        {processing && (
          <GSC.FadeInContainer>
            <Processing capturedImage={capturedImage} />
          </GSC.FadeInContainer>
        )}
        {serverErrorFailReason && (
          <CaptureServerErrorOverlay
            failReason={serverErrorFailReason}
            message={serverErrorMessage}
            onRetry={retryCapture}
            retryable={serverErrorRetryable}
            unverifiedUrl={state.customerData?.redirect?.unverifiedUrl ?? ''}
          />
        )}
      </GSC.RelativeContainer>
      <Spacer $top={1}>
        <SecuredByBadge />
      </Spacer>
      {config.photoUploadEnabled && (
        <ManualMessageUpload
          id="smile_liveness_manual_upload"
          onComplete={(content) => {
            setProcessing(true);
            void upload(content);
          }}
        />
      )}
    </>
  );
};
